import React, { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { SEND_MESSAGE_V2 } from '../../../graphql/operations';
import './widgets.css';

/**
 * ConfirmationCard — shows a pending action that requires user approval.
 *
 * Option B: after Confirm, the card shows optimistic "Approved — In progress" locally
 * (no separate server progress widget). When an assistant message arrives after this
 * message, the header reconciles to a completed state.
 *
 * widgetData structure (from build_confirmation_card):
 * {
 *   type: "Card",
 *   status: { text: "Pending approval", icon: "clock" },
 *   confirm: { label: "Confirm", action: { type: "record.confirm_action", payload: { pendingActionId } } },
 *   cancel: { label: "Cancel", action: { type: "record.discard_action", payload: { pendingActionId } } },
 *   children: [...]
 * }
 */
function ConfirmationCard({
  data,
  widgetActionUserId,
  currentUserId,
  messageId,
  conversationId,
  hasAssistantFollowUp,
  onConfirmationSubmitted
}) {
  const [submissionState, setSubmissionState] = useState('idle');
  const [sendMessage] = useMutation(SEND_MESSAGE_V2);

  const children = data.children || [];
  const confirmAction = data.confirm;
  const cancelAction = data.cancel;
  const pendingStatusText = data.status?.text || 'Pending';
  const canAct = currentUserId && widgetActionUserId && currentUserId === widgetActionUserId;
  const pendingActionId =
    confirmAction?.action?.payload?.pendingActionId
    || cancelAction?.action?.payload?.pendingActionId
    || null;

  const persistedStateKey = useMemo(() => {
    if (!conversationId || !pendingActionId) return null;
    const suffix = messageId ? `${pendingActionId}:${messageId}` : pendingActionId;
    return `kapsa:confirmation-state:v1:${conversationId}:${suffix}`;
  }, [conversationId, pendingActionId, messageId]);

  useEffect(() => {
    if (!persistedStateKey || typeof window === 'undefined') return;
    const persisted = window.sessionStorage.getItem(persistedStateKey);
    if (
      persisted === 'confirmed_in_progress'
      || persisted === 'confirmed_complete'
      || persisted === 'discarded'
      || persisted === 'error'
    ) {
      setSubmissionState(persisted);
    }
  }, [persistedStateKey]);

  useEffect(() => {
    if (!persistedStateKey || typeof window === 'undefined') return;
    if (
      submissionState === 'confirmed_in_progress'
      || submissionState === 'confirmed_complete'
      || submissionState === 'discarded'
    ) {
      window.sessionStorage.setItem(persistedStateKey, submissionState);
      return;
    }
    if (submissionState === 'idle' || submissionState === 'error') {
      window.sessionStorage.removeItem(persistedStateKey);
    }
  }, [persistedStateKey, submissionState]);

  // Extract title, badge, texts, captions from children
  const title = children.find((c) => c.type === 'Title');
  const badge = children.find((c) => c.type === 'Badge');
  const caption = children.find((c) => c.type === 'Caption');
  const textItems = children.filter(
    (c) => c.type === 'Text' && c !== title && c !== badge && c !== caption
  );

  const handleAction = async (action) => {
    if (!action?.action?.payload?.pendingActionId) return;
    setSubmissionState('sending');
    const actionType = action.action.type;
    const isConfirm = actionType === 'record.confirm_action';
    const pendingActionId = action.action.payload.pendingActionId;
    try {
      await sendMessage({
        variables: {
          conversationId,
          content: isConfirm ? `/confirm ${pendingActionId}` : `/discard ${pendingActionId}`
        }
      });
      setSubmissionState(isConfirm ? 'confirmed_in_progress' : 'discarded');
      if (isConfirm) {
        onConfirmationSubmitted?.();
      }
    } catch (err) {
      console.error('Failed to send confirmation response:', err);
      setSubmissionState('error');
    }
  };

  const confirmComplete = (
    submissionState === 'confirmed_complete'
    || (submissionState === 'confirmed_in_progress' && hasAssistantFollowUp)
  );
  const confirmInFlight =
    submissionState === 'confirmed_in_progress' && !hasAssistantFollowUp;
  const showWorkingIndicator = submissionState === 'sending' || confirmInFlight;

  useEffect(() => {
    if (submissionState === 'confirmed_in_progress' && hasAssistantFollowUp) {
      setSubmissionState('confirmed_complete');
    }
  }, [submissionState, hasAssistantFollowUp]);

  let headerClass = 'widget-header-warning';
  let statusLine = pendingStatusText;
  let badgeLabel = badge?.label;
  let badgeColor = badge?.color || 'warning';

  if (submissionState === 'sending') {
    statusLine = pendingStatusText;
  } else if (submissionState === 'discarded') {
    headerClass = 'widget-header-neutral';
    statusLine = 'Canceled';
    badgeLabel = 'Request canceled';
    badgeColor = 'neutral';
  } else if (confirmComplete) {
    headerClass = 'widget-header-success';
    statusLine = 'Approved';
    badgeLabel = 'Complete';
    badgeColor = 'success';
  } else if (confirmInFlight) {
    headerClass = 'widget-header-warning';
    statusLine = 'Approved';
    badgeLabel = 'In-Progress';
    badgeColor = 'warning';
  } else if (submissionState === 'error') {
    headerClass = 'widget-header-warning';
    statusLine = pendingStatusText;
  }

  const showPendingActions =
    canAct && (submissionState === 'idle' || submissionState === 'error');

  const showLockedConfirmActions =
    canAct && confirmInFlight && confirmAction && cancelAction;

  return (
    <div className="widget-card">
      <div className={`widget-card-status-header ${headerClass}`}>
        <span className="widget-status-dot" />
        <span className="widget-status-text">{statusLine}</span>
        {showWorkingIndicator && (
          <span className="widget-status-text" aria-hidden> ⏳</span>
        )}
      </div>

      <div className="widget-card-content">
        {title && <h4 className="widget-card-title">{title.value}</h4>}
        {badgeLabel && (
          <span className={`widget-badge widget-badge-${badgeColor}`}>{badgeLabel}</span>
        )}

        {textItems.map((item, i) => (
          <p key={i} className={`widget-text ${item.weight === 'semibold' ? 'widget-text-bold' : ''}`}>
            {item.value}
          </p>
        ))}

        {caption && submissionState === 'idle' && <p className="widget-caption">{caption.value}</p>}

        {showPendingActions && (
          <div className="widget-card-actions">
            {confirmAction && (
              <button
                type="button"
                className="widget-btn widget-btn-confirm"
                onClick={() => handleAction(confirmAction)}
              >
                {confirmAction.label || 'Confirm'}
              </button>
            )}
            {cancelAction && (
              <button
                type="button"
                className="widget-btn widget-btn-cancel"
                onClick={() => handleAction(cancelAction)}
              >
                {cancelAction.label || 'Cancel'}
              </button>
            )}
          </div>
        )}

        {showLockedConfirmActions && (
          <div className="widget-card-actions">
            <button type="button" className="widget-btn widget-btn-confirm is-done" disabled>
              {confirmAction.label || 'Confirm'}
            </button>
            <button type="button" className="widget-btn widget-btn-cancel" disabled>
              {cancelAction.label || 'Cancel'}
            </button>
          </div>
        )}

        {submissionState === 'sending' && (
          <p className="widget-caption">Sending response...</p>
        )}

        {confirmInFlight && submissionState !== 'sending' && (
          <p className="widget-caption">Updating…</p>
        )}

        {confirmComplete && (
          <p className="widget-caption widget-responded">Done</p>
        )}

        {submissionState === 'discarded' && (
          <p className="widget-caption">This request was canceled.</p>
        )}

        {submissionState === 'error' && (
          <p className="widget-caption" role="alert">
            Could not send your response. Try again.
          </p>
        )}

        {!canAct && submissionState === 'idle' && (
          <p className="widget-caption">Only the requesting user can approve this action.</p>
        )}
      </div>
    </div>
  );
}

export default ConfirmationCard;
